<script lang="ts" context="module">
	export type ObservedRestaurant = Observable<{
		loading?: boolean,
		payload: Partial<RestaurantExposed & RestaurantSemiStatic & RestaurantDynamic>,
		error?: any,
		deals?: RestaurantDeal[],
		fromLI?: boolean,
		outOfBounds?: boolean,
	}>;

	// TODO: Not ideal
	export function retypeItem(i: unknown) {
		return i as PI & { section_id: Section["id"] };
	}


	export function isBOGOF(i: PI) {
		return "menu_deal_action" in i && i.menu_deal_action === "buy_1_get_1_free";
	}

	/**
	 * This is only for BOGOF items, otherwise returns null
	 * 
	 * In the case of BOGOF items there are "virtual/fake" items in the menu.
	 * These "virtual/fake" items change their ids every menu request, but there is a "non-virtual/real" one in the menu.
	 * This function finds the "non-virtual/real" item in the menu.
	 * @param item
	 * @param menu
	 */
	export function findRealItem(item: PI, menu?: Menu): PI & { section_id: number } | null {
		if (!menu || !isBOGOF(item)) return null;

		let result = null;
		for (const section of menu.sections) {
			if (result) break;

			for (const i of section.items) {
				if (result) break;

				if (i.id === item.original_item_id) {
					result = {
						...i,
						section_id: section.id,
					}
				}
			}
		}
		return result;
	}
</script>

<script lang="ts">
	import type { RestaurantDynamic, RestaurantSemiStatic } from "$lib/def/landing";
	import type { Cache } from "$lib/serv/core/cache";
	import type { PartRestaurant, RestaurantExposed } from "$lib/serv/restaurant";
	import type { Page } from "@sveltejs/kit";
	import type { Address } from "proto/address";
	import type { Availability, ExtraGroup, Menu, Item as PI, Section, Variant } from "proto/menu";
	
	import { browser } from "$app/environment";
	import { KEY_VERTICAL, Vertical } from "$lib/def/verticals";
	import { checkForModifierPrice } from "$lib/serv/food-cart";
	import { KEY_FOOD_ITEM, KEY_SEARCH, type RestaurantDeal } from "$lib/serv/food-landing";
	import { browsingLocation$, LOCATION_PROPS } from "$lib/serv/locations";
	import { getRestaurantExposed, getRestaurantState } from "$lib/serv/restaurant";
	import { AlertType } from "$lib/serv/toast";
	import { KEY_SECTION } from "$lib/ui/layout/SectionedListView.svelte";
	import { addressModalIsOpen$, selectAddress } from "$lib/ui/modals/AddressModal.svelte";
	import { formatCurrency } from "$lib/util/format";
	import { deepCopy } from "$lib/util/object";
	import { RxWritable } from "$lib/util/obs";
	import { ScrollSpy } from "$lib/util/scrollspy";
	import { hasParam, query, queryFrom } from "$lib/util/url";
	import {
	    distinctUntilChanged,
	    filter,
	    firstValueFrom,
	    map,
	    Observable, of,
	    Subject,
	    switchMap
	} from "rxjs";
	import { onDestroy } from "svelte";
	import { fly } from "svelte/transition";
	import { getMenuDealLabel, makeRestPDPSlug } from "./server";
	import { attHref } from "$lib/util/ui";
	import { hasGeo } from "$lib/util/address";

	import Highlights from "$lib/ui/home/food/menu/Highlights.svelte";
	import RestaurantHeader from "$lib/ui/home/food/menu/RestaurantHeader.svelte";
	import SearchInput from "$lib/ui/home/SearchInput.svelte";
	import Catalogue from "$lib/ui/home/shop/Catalogue.svelte";
	import SearchResults from "$lib/ui/home/shop/SearchResults.svelte";
	import Sidenav from "$lib/ui/layout/Sidenav.svelte";
	import SidenavHeader from "$lib/ui/layout/SidenavHeader.svelte";
	import Alert from "$lib/ui/shared/Alert.svelte";
	import Error from "$lib/ui/shared/Error.svelte";
	import LazyBackgroundImage from "$lib/ui/shared/LazyBackgroundImage.svelte";
import { profile$ } from "$lib/serv/core/auth";

	export let restaurant: PartRestaurant;
	export let deals: RestaurantDeal[] | undefined;
	export let menu$: Observable<Cache<Menu>>;
	export let outOfBounds = false;
	export let loading = false;
	export let error: any = undefined;
	export let sectionFocus: string | null = null;
	export let location: Partial<Address>;
	export let backHref: string;

	const placeholder$ = getRestaurantExposed(restaurant.id ?? 0).pipe(
		map((exposed) => {
			if (exposed.address.suburb) return `Search ${restaurant.name}, ${exposed.address.suburb}`; 
			return `Search ${restaurant.name}`;
		}),
	);

	export let page: Page;

	// const id$ = new Subject<typeof id>();
	// $: if (id) id$.next(id);

	const loc$ = new Subject<typeof location>();
	$: loc$.next(location);
	
	type Item = PI & { variants: (Variant & { extras?: ExtraGroup[] })[] }
	let menuitem: Item | null; // open/selected menu item

	$: locObj = location as Record<string, string>;

	const search$ = new RxWritable<string>("");
	$: search$.next(query(page.url).get('search')?.toString());

	/**
	 * Sends a signal just before menu$'s first emit in response to user initiating search.  Needed
	 * because if we rely on search$ for this, search$ emits much more quickly than menu$ and as a
	 * result, the processing that the template does for searches first happens on the full menu.
	 * On big menus, the UI freezes a bit as a result.
	*/
	const searching$ = new Subject<boolean>();

	const filterMenu = (sections: Section[], search: string) => {
		if (!search) return sections;

		search = search.toLowerCase();

		const matchesSearch = (item: Item) => {
			return item.name?.toLowerCase().includes(search)|| item.description?.toLowerCase().includes(search);
		}
		return sections?.filter((section: Section) => {
			return section?.items.some(matchesSearch);
		}).map((section: Section) => {
			const items = section?.items.filter(matchesSearch);
      // return JSON.parse(JSON.stringify({ ...section, items }));
      return deepCopy({}, { ...section, items });
		});
	}

	const menuResults$ = menu$.pipe(
		switchMap(loader => search$.pipe(
			// debounceTime(1000), // there is already a debounce from the input to the url change
			distinctUntilChanged(),
			map(search => [loader, search] as [Cache<Menu>, string]),
		)),
		switchMap(([loader, search]) => {
			if (search) {
				searching$.next(true);
				const sections = filterMenu(loader.payload?.sections as Section[], search);
				return of({ payload: { ...loader.payload, sections }});
			}

			searching$.next(false);
			return of(loader);
		}),
	) as Observable<Cache<Menu>>;

	function allVariantsSoldOut(item: Item) {
		return !item.variants.find((v) => !!v.availability || (v.availability && (v.availability as Availability).status === 'visible'));
	}

	function getSectionLink(
		sectionId: Section["id"],
		location: Partial<Address>,
	) {
		return queryFrom(location as Record<string, string>).set(KEY_SECTION, sectionId.toString()).toUrl();
	}

	function getPillActive(
		sectionId: Section["id"],
		pageUrl: URL,
	) {
		return query(pageUrl).hasValue(KEY_SECTION, sectionId);
	}

	function getFoodItemLink(
		item: PI & { section_id: Section["id"] } | null,
		menuId: number | undefined,
		restaurantId: number | undefined,
		pageUrl: URL,
	) {
		if (!item || !menuId) return "";
		// TODO: may need to change to a full url using something like `navPDP`
		return query(pageUrl).set(KEY_FOOD_ITEM, makeRestPDPSlug(item.id, item.section_id, menuId, restaurantId??'')).toUrl();
	}

	// Store for active menu section
  let activeMenuSection: number | null = null;
	
	const scrollspy = new ScrollSpy((id) => {
		activeMenuSection = id ? +id.split('-')[1] : null;
	});
	
	function addSection(e: HTMLElement) {
		scrollspy.addItem(e);
		return {
			destroy: () => scrollspy.removeItem(e)
		}
	}

	let prevScrollId: string | null = null;
	$: {
		if (browser) {
			setTimeout(() => {
				const q = query(page.url);
				if (q.has(KEY_SECTION)) {
					const id = q.get(KEY_SECTION);
					if (id !== prevScrollId) {
						scrollspy.scrollTo(`section-${id}`);
						prevScrollId = id;
					}
				}
			}, 100)
		}
	}

	onDestroy(() => {
		scrollspy.destroy();
	});

	let openMenuModal = false;

</script>


<SearchInput placeholder={$placeholder$} currentPageUrl={page.url} />

<Sidenav {page} bind:open={openMenuModal}>
	{#snippet header()}
		{#if restaurant}
			<SidenavHeader name={restaurant.name ?? ''} logo={restaurant.images?.restaurant_logo} estimate={getRestaurantState(restaurant).text} />
		{/if}
	{/snippet}
	{#if $menu$}
		{@const { payload: menu } = $menu$}
		{#if query(page.url).hasValue('vertical', Vertical.Store) && query(page.url).has(KEY_SECTION) || query(page.url).has(KEY_SEARCH)}
			<a href={`?${query(page.url).keep(...LOCATION_PROPS, KEY_VERTICAL).remove(KEY_SEARCH, KEY_SECTION)}`} class="label-xl book ellipses" in:fly={{ x: 20, delay: 1 * 10 }} out:fly|global={{ x: -20, delay: 1 * 10 }}><span class="icon-house-20"></span> Browse all</a>
		{/if}
			{#each menu?.sections ?? [] as section, i}
				{@const href = query(page.url).hasValue('vertical', Vertical.Store) ? query(page.url).toggle(KEY_SECTION, section.id).toUrl() : query(page.url).remove(KEY_SEARCH).set(KEY_SECTION, section.id.toString()).toUrl()}
				{@const active = query(page.url).hasValue('vertical', Vertical.Store) ? section.id.toString() === query(page.url).get('section') : section.id.toString() === activeMenuSection?.toString()}
				<a
					{href}
					data-sveltekit-replacestate={query(page.url).has(KEY_SECTION)}
					on:click={() => openMenuModal = false}
					class="label-xl book ellipses"
					class:active in:fly={{ x: 20, delay: i * 20 }}
					out:fly|global={{ x: -20, delay: i * 20 }}
					data-sveltekit-noscroll
				>{section.name}</a>
			{/each}
	{/if}
</Sidenav>
	

<div class="restaurant relative z-index-0">

	<!-- TODO: Loading skeleton?  For when detail values are undefined -->


	{#if !loading && !restaurant}
		<Error title="No data found" buttonText="Home" buttonHref="/" />
	{/if}

	{#if !loading && restaurant && outOfBounds}
		{#if hasGeo(location)}
		<Alert alert={{ type: AlertType.Action, title: `${restaurant.name} does not currently deliver to your address`, button: 'Browse your area', onDismiss: queryFrom(locObj).keep(...LOCATION_PROPS, 'vertical').toUrl('/')  }} />
		{:else}
		<Alert alert={{ type: AlertType.Action, title: `Add your address to order from ${restaurant.name}`, button: 'Add address', onDismiss: () => selectAddress(true) }} /><!-- pickAddress() instead -->
		{/if}
	{/if}
	
	<!-- <Search bind:value={$search$} placeholder={`Search ${$rest$?.payload.name}`}/> -->

	<!-- Should always be visible for restaurants, but hidden for shops if we on a grid view  -->
	{#if query(page.url).has(KEY_SEARCH) || query(page.url).hasValue('vertical', Vertical.Store) && hasParam(page.url, KEY_SECTION)}
		<!-- hidden for search results and selected shop sections -->
	{:else}
		<RestaurantHeader {backHref} {loading} {restaurant} {deals} {error} />
	{/if}

	{#if $menuResults$}
		{@const isShop = query(page.url).hasValue('vertical', Vertical.Store)}
		{@const { loading, payload: menu } = $menuResults$}
		{@const searching = $searching$}
		{@const hasSectionId = hasParam(page.url, KEY_SECTION)}

		<!-- Should always be visible for restaurants, but hidden for shops if we are searching or on a grid view -->
		<!-- {#if (!isShop && !query($page.url).has(KEY_SEARCH)) || !searching && !sectionId} -->
		{#if !hasParam(page.url, KEY_SEARCH)}
			{#if !hasSectionId || hasSectionId && query(page.url).hasValue('vertical', Vertical.Restaurant)}
				<Highlights
					{loading}
					{menu}
					{restaurant}
					antiCrawl={!hasGeo(location) && !$profile$}
					getItemLink={(item) => getFoodItemLink(item, menu?.id, restaurant.id, page.url)}	
				/>
			{/if}
		{/if}

		<!-- On restaurants, should only be visible if we are not searching.  On shops, should only be visible if we are not searching and not on a grid view. -->
		<!-- {#if (!isShop && !searching) || (isShop && !searching && !sectionId)}
			<SectionPills
				cache={$menu$}
				getSectionLink={(s) => getSectionLink(s, location)}
				getPillActive={(s) => getPillActive(s, $page.url)}
				collapsible={isShop}
				noScroll={restaurant.vertical !== Vertical.Store}
			/>
		{/if} -->

		{#if isShop}
		
			{#if searching}
				<SearchResults
					cache={{ loading, payload: menu }}
					{page}
					getItemLink={(item) => getFoodItemLink(item, menu?.id, restaurant.id, page.url)}
				/>
			{:else}
				<Catalogue
					cache={$menu$}
					storeId={restaurant.id ?? 0}
					storeName={restaurant.name ?? ''}
					pageUrl={page.url}
					sectionId={sectionFocus ? +sectionFocus : null}
					showSeeAllBtn={!searching}
				/>
			{/if}

		{:else if menu?.sections}
      {@const sections = sectionFocus && query(page.url).hasValue('vertical', Vertical.Store) ? menu.sections.filter((s) => s.id.toString() === sectionFocus) : menu.sections} 
			{#each sections as section}
				<article class="menu-section" use:addSection id={`section-${section.id}`}>
					<a
						class="item-tab flex-row justify-between align-center"
						href={'?' + query(page.url).toggle(KEY_SECTION, section.id.toString()).toString()}
						class:active={query(page.url).hasValue(KEY_SECTION, section.id.toString())}
						data-sveltekit-noscroll>

						<h2 class="headline-m bold">{section.name}</h2>
						<div class="flex-row align-center">
							<span>{section.items.length}</span>
							<span class={`icon-chevron-${query(page.url).hasValue(KEY_SECTION, section.id.toString()) ? 'up' : 'down'}-28`}></span>
						</div>
					</a>
					
					<div class="grid cousine m-medium">
						{#if section.items}

							{#each section.items as item}
							{@const priced = checkForModifierPrice(item, menu.options, menu.extras, menu.addons)}
							{@const realItem = findRealItem(item, menu) ?? retypeItem(item)}
							{@const nolink = !hasGeo(location) && !$profile$}
								<a
									href="#"
									use:attHref={{path:getFoodItemLink(realItem, menu.id, restaurant.id, page.url), nolink}}
									rel="nofollow"
									class="item mobile medium-gap flex align-start"
									data-testid={`item_${item.id}_${section.id}`}
									data-sveltekit-noscroll>
									<div class="text fill-space">
										<h4 class="title label-xl bold">{item.name}</h4>
										<!-- <mark>{item.variants.length > 1 ? 'has variants!!' : ''}</mark> -->
										{#if item.variants.length}
											<p class="price body-s bold m-0">
												{#if !item.variants[0].old_price}
													<span class="label-l book grey">from</span>&nbsp;
													{formatCurrency(item.variants[0].price)}
												{:else}
													<span style="margin-right: 0.4rem;" class="bold red">{formatCurrency(item.variants[0].price)}</span>
													<span class="grey book strike-through">{formatCurrency(item.variants[0].old_price)}</span>
												{/if}
												{#if item.collect_only}
													<span class="pill small label-s bold m-0">Collect only</span>
												{/if}
												{#if item.menu_deal_action}
													<br>
													<span class="label-s bold promo-pill m-0">{getMenuDealLabel(item.menu_deal_action)}</span>
												{/if}
											</p>
										{/if}
										<p class="body body-s book grey ellipses-3">{item.description ?? ''}</p>
										<!-- <highlight>item.collect_only: {item.collect_only}</highlight> -->
									</div>
									{#if item.media}
										<LazyBackgroundImage image={[item.media, 300]} alt={item.name}>
											<div class="quantity">
												<button>
													<span class="body-l bold add"><span>Add</span></span>
													<span class="icon-add-28 vertical-middle"></span>
												</button>
											</div>
											{#if item.availability?.status === 'sold_out' || allVariantsSoldOut(item)}
												<article class="cover">
													<span class="label-s bold nowrap">Sold out</span>
												</article>
											{/if}
										</LazyBackgroundImage>
									{:else}
										<div class="quantity">
											<button>
												<span class="body-l bold add"><span>Add</span></span>
												<span class="icon-add-28 vertical-middle"></span>
											</button>
										</div>
									{/if}
								</a>
							{/each}
							
						{/if}
					</div>
				</article>
			{:else}
				<p class="body-m grey">No items match your search - please try something else.</p>
			{/each}
		{/if}
	{/if}
</div>

<style lang=scss>
	@use 'screen';
	@use 'color';
	
	.menu-section {
		@media(min-width: 920px) { margin-bottom: 2rem; }
		.item-tab {
			padding: 1rem;
			background: color.$mobile-rest-item;
			border-radius: 14px;
			@media(min-width: 920px) {
				border-bottom: unset;
				position: sticky;
				top: 8.9rem;
				background: color.$background-elevation-3dp;
				z-index: 2;
				padding: 1rem 0;
				border-radius: 0;
			}
			@media(screen.$lg) { 
				top: 7.9rem;
				padding: 1.2rem 0;
			}
			>h2 { margin: 0; }
			>div {
				margin-left: 1rem;
				span {
					&:nth-of-type(1) {
						font-size: 1.2rem;
						color: color.$on-background-medium-importance;
						margin-right: .5rem;
						margin-top: -2px;
					}
				}
				@media(min-width: 920px) { display: none; }
			}
			&.active {
				position: sticky;
				top: 8.4rem;
				border-radius: 0 0 14px 14px;
				z-index: 2;
				@media(min-width: 920px) {
					border-radius: 0;
					top: 8.9rem;
				}
				@media(screen.$lg) { 
					top: 7.9rem;
					padding: 1.2rem 0;
				}
				~ .grid {
					margin-bottom: 24px;
				}
			}
		}
	}
</style>
